import React from "react";
import { useTranslation } from "react-i18next";

import { Box, Text, Link } from "@chakra-ui/react";

import airLuggage from "assets/images/air-bundle/airLuggage.png";
import airPayout from "assets/images/air-bundle/airPayout.png";
import InfoModal from "components/Journey/AirBundle/InfoModal";
import { AirLuggageJourney } from "models/journeys/AirLuggageJourney";
import { AirPayoutJourney } from "models/journeys/AirPayoutJourney";
import { policyUrl } from "utils/insurancPolicy";

import AirBundleTagStatus from "../AirBundleTagStatus";
import { IBoxTag } from "../AirBundleTagStatus/AirBundleTagStatus";

interface IBoxContent {
  description: string;
  additionalContent?: JSX.Element;
}

interface IComponent {
  boxTag: IBoxTag;
  boxContent: IBoxContent;
  mainAirPayoutJourney?: AirPayoutJourney;
  airLuggageJourney?: AirLuggageJourney;
}

const ProtectBoxContent: React.FC<IComponent> = ({ boxTag, boxContent, airLuggageJourney, mainAirPayoutJourney }) => {
  const { t } = useTranslation();

  const isAirPayout = !!mainAirPayoutJourney;
  const ipidVersion = isAirPayout ? mainAirPayoutJourney?.ipidVersion : airLuggageJourney?.ipidVersion;
  const linkToFile = policyUrl(isAirPayout, ipidVersion);

  const testId = isAirPayout ? "protect-box-tag-airpayout" : "protect-box-tag-airluggage";

  const airPayoutModalContent = () => {
    const isCancelationOnly = mainAirPayoutJourney?.cancelationOnly;
    const isOutboundOnly = mainAirPayoutJourney?.outboundOnly;
    const airPayoutPayout = `${mainAirPayoutJourney?.payoutAmount.toString()}`;
    const getAirPayoutDescription =
      isOutboundOnly && isCancelationOnly
        ? t("air_payout.modal.main.description_outbound", { amount: airPayoutPayout })
        : t("air_payout.modal.main.description", { amount: airPayoutPayout });
    return [
      {
        title: t("air_payout.title"),
        description: getAirPayoutDescription,
        benefits: [t("air_payout.modal.main.no_forms"), t("air_payout.modal.main.no_signatures")],
      },
      {
        title: t("air_payout.modal.main.process"),
        benefits: [t("air_payout.modal.main.detect_and_notify"), t("air_payout.modal.main.provide_bank_details")],
      },
    ];
  };

  const airLuggageModalContent = () => {
    const maxBags = airLuggageJourney?.maxBags;
    const airLuggageDescriptionKey = maxBags === 1 ? "subtitle" : "title";
    const airLuggageBenefitsKey = maxBags === 1 ? "extra_money" : "if_your_checked_in_luggage";
    const airLuggagePayout = `${airLuggageJourney?.payoutAmount.toString()}`;
    return [
      {
        title: t("air_luggage.title"),
        description: t(`air_luggage.modal.main.${airLuggageDescriptionKey}`, {
          amount: airLuggagePayout,
          bags: maxBags,
        }),
        benefits: [
          t(`air_luggage.modal.main.${airLuggageBenefitsKey}`, { amount: airLuggagePayout, bags: maxBags }),
          t("air_luggage.modal.main.no_proof"),
        ],
      },
      {
        title: t("air_payout.modal.main.process"),
        benefits: [t("air_luggage.modal.main.if_your_checked_in_bag"), t("air_luggage.modal.main.submit_the_pir")],
        infoBoxText: t("air_luggage.modal.main.do_not_leave_the_luggage"),
      },
    ];
  };

  const readMoreModal = (
    <InfoModal
      modalContent={isAirPayout ? airPayoutModalContent() : airLuggageModalContent()}
      linkToFile={linkToFile}
      image={isAirPayout ? airPayout : airLuggage}
      render={(openModalHandler) => (
        <Box mt={3}>
          <Link
            fontSize="sm"
            fontWeight="light"
            color="primary.500"
            onClick={openModalHandler}
            _visited={{ color: "greyscale.100" }}
            _focus={{ boxShadow: "none" }}
            data-testid={isAirPayout ? "air-payout-read-more-button" : "air-luggage-read-more-button"}
          >
            {t("air_payout.card.read_more")}
          </Link>
        </Box>
      )}
    />
  );

  return (
    <Box border="1px solid" borderColor="greyscale.400" borderRadius="lg" width="100%">
      <Box py={4} px={4}>
        <AirBundleTagStatus statusBox={boxTag} dataTestId={testId} />
        <Text fontSize="lg" fontWeight="semibold" mt={3} mb={3} px={0}>
          {isAirPayout ? t("air_payout.title") : t("air_luggage.title")}
        </Text>
        <Box color="greyscale.700" fontSize="sm">
          {boxContent.description}
          {readMoreModal}
        </Box>
      </Box>
      {boxContent.additionalContent && boxContent.additionalContent}
    </Box>
  );
};

export default ProtectBoxContent;
